<template>
    <UiDrawerPanel
        v-if="manualShared && drawer.opened.value"
        class="manual-drawer"
        :title="drawer.page.value?.title ?? lang('manual::manual.title')"
        @close="drawer.close()"
        @keydown.esc="drawer.close()"
    >
        <div v-if="drawer.loading.value" class="manual-drawer__loading">…</div>

        <template v-else-if="drawer.page.value">
            <ManualBreadcrumbs
                v-if="drawer.page.value.breadcrumb.length"
                :items="drawer.page.value.breadcrumb"
                @select="drawer.loadPage($event)"
            />
            <UiAlert v-if="drawer.page.value.origin_changed" variant="warning">
                {{ lang('manual::manual.origin_changed') }}
            </UiAlert>
            <UiAlert v-if="drawer.page.value.origin_locale" variant="info">
                {{ lang('manual::manual.english_only') }}
            </UiAlert>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div ref="contentEl" class="manual-content" v-html="drawer.page.value.html" />
            <ManualChildPages :items="drawer.page.value.children" @select="drawer.loadPage($event)" />
        </template>

        <template v-else>
            <p class="manual-drawer__empty">{{ lang('manual::manual.no_context') }}</p>
            <Field
                v-model="query"
                :field="searchField"
                label-position="top"
                size="sm"
                dusk="manual-drawer-search"
                class="mb-2"
            />
            <ManualSearchResults
                v-if="drawer.searchResults.value"
                :results="drawer.searchResults.value"
                @select="drawer.loadPage($event)"
            />
            <ManualTree v-else :nodes="drawer.tree.value" @select="drawer.loadPage($event)" />
        </template>

        <template v-if="drawer.page.value" #footer>
            <div class="manual-drawer__actions">
                <Button :a="true" :href="route('manual.reader.show', { page: drawer.page.value.uid })" size="sm">
                    {{ lang('manual::manual.open_reader') }}
                </Button>
                <Button
                    v-if="manualShared.canEdit"
                    :a="true"
                    :href="route('manual/pages.admin.edit', { id: drawer.page.value.id })"
                    color="primary"
                    size="sm"
                >
                    {{ lang('manual::manual.edit_page') }}
                </Button>
            </div>
        </template>
    </UiDrawerPanel>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { lang } from '@/lang/composable';
import UiAlert from '@src/Common/Components/UiAlert.vue';
import ManualBreadcrumbs from '@src/Manual/Pages/Partials/ManualBreadcrumbs.vue';
import ManualChildPages from '@src/Manual/Pages/Partials/ManualChildPages.vue';
import ManualTree from '@src/Manual/Pages/Partials/ManualTree.vue';
import ManualSearchResults from '@src/Manual/Pages/Partials/ManualSearchResults.vue';
import { useManualDrawer } from '@src/Manual/Pages/composables/useManualDrawer';
import { useManualEmbeds } from '@src/Manual/Pages/composables/useManualEmbeds';

const drawer = useManualDrawer();
const query = ref('');
const contentEl = ref<HTMLElement | null>(null);
const embeds = useManualEmbeds();

// contentEl is part of the source: closing the drawer (v-if) drops the DOM
// with the mounted islands — reopening with unchanged html must remount.
watch([contentEl, () => drawer.page.value?.html], async () => {
    await nextTick();
    embeds.mountEmbeds(contentEl.value);
});

const onLocationChanged = (): void => drawer.onRouteChanged();

onMounted(() => drawer.installHistoryListener(onLocationChanged));

onBeforeUnmount(() => {
    drawer.removeHistoryListener(onLocationChanged);
    embeds.unmountEmbeds();
});

const manualShared = computed(() => usePage().props.manual as { route?: string | null; canEdit?: boolean } | undefined);

const searchField = {
    type: 'text',
    slug: 'manual-drawer-search',
    name: '',
    placeholder: lang('manual::manual.search'),
};

let searchTimer: ReturnType<typeof setTimeout> | undefined;

watch(query, () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => void drawer.search(query.value), 250);
});

watch(
    () => manualShared.value?.route,
    () => drawer.onRouteChanged(),
);
</script>

<style scoped>
.manual-drawer__loading {
    opacity: 0.5;
}
.manual-drawer__empty {
    opacity: 0.6;
    margin-bottom: 0.75rem;
}
.manual-drawer__actions {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
}
.manual-content :deep(h2) {
    font-size: 1rem;
    font-weight: 600;
    margin: 1rem 0 0.25rem;
}
.manual-content :deep(h3) {
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0.75rem 0 0.25rem;
}
.manual-content :deep(h4) {
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0.5rem 0 0.25rem;
}
.manual-content :deep(p) {
    margin: 0.375rem 0;
}
.manual-content :deep(a) {
    text-decoration: underline;
}
.manual-content :deep(ul) {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0.375rem 0;
}
.manual-content :deep(ol) {
    list-style: decimal;
    padding-left: 1.25rem;
    margin: 0.375rem 0;
}
.manual-content :deep(li) {
    margin: 0.25rem 0;
}
.manual-content :deep(li > ul),
.manual-content :deep(li > ol) {
    margin: 0.25rem 0;
}
.manual-content :deep(img) {
    max-width: 100%;
    border-radius: 0.375rem;
    border: 1px solid rgba(128, 128, 128, 0.25);
}
.manual-content :deep(.manual-callout) {
    border: 1px solid rgba(128, 128, 128, 0.25);
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
    margin: 0.5rem 0;
}
.manual-content :deep(.manual-callout--info) {
    border-color: rgba(59, 130, 246, 0.35);
    background: rgba(59, 130, 246, 0.08);
}
.manual-content :deep(.manual-callout--tip) {
    border-color: rgba(34, 197, 94, 0.35);
    background: rgba(34, 197, 94, 0.08);
}
.manual-content :deep(.manual-callout--warning) {
    border-color: rgba(245, 158, 11, 0.35);
    background: rgba(245, 158, 11, 0.08);
}
.manual-content :deep(.manual-callout--danger) {
    border-color: rgba(239, 68, 68, 0.35);
    background: rgba(239, 68, 68, 0.08);
}
.manual-content :deep(.manual-callout-title) {
    font-weight: 600;
}
.manual-content :deep(.manual-steps) {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0;
    counter-reset: manual-step;
}
.manual-content :deep(.manual-step) {
    position: relative;
    padding: 0 0 0.75rem 2.25rem;
    counter-increment: manual-step;
}
.manual-content :deep(.manual-step)::before {
    content: counter(manual-step);
    position: absolute;
    left: 0;
    top: 0;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 999px;
    background: rgba(128, 128, 128, 0.12);
    border: 1px solid rgba(128, 128, 128, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
}
.manual-content :deep(.manual-flow) {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin: 0.5rem 0;
}
.manual-content :deep(.manual-flow-node) {
    border: 1px solid rgba(128, 128, 128, 0.25);
    border-radius: 0.625rem;
    padding: 0.5rem 0.75rem;
    text-align: center;
}
.manual-content :deep(.manual-flow-node b) {
    display: block;
    font-weight: 600;
}
.manual-content :deep(.manual-flow-node small) {
    opacity: 0.6;
}
.manual-content :deep(.manual-flow-arrow) {
    display: grid;
    place-items: center;
    font-size: 1.25rem;
    opacity: 0.5;
    transform: rotate(90deg);
}
.manual-content :deep(.manual-table table) {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
}
.manual-content :deep(.manual-table th),
.manual-content :deep(.manual-table td) {
    border: 1px solid rgba(128, 128, 128, 0.25);
    padding: 0.25rem 0.5rem;
    text-align: left;
    vertical-align: top;
}
.manual-content :deep(.manual-table th) {
    background: rgba(128, 128, 128, 0.08);
    font-weight: 600;
}
.manual-content :deep(.manual-screenshot figcaption) {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: 0.25rem;
}
.manual-content :deep(.manual-data-origin) {
    border-left: 3px solid rgba(128, 128, 128, 0.4);
    background: rgba(128, 128, 128, 0.06);
    padding: 0.5rem 0.75rem;
    margin: 0.5rem 0;
    border-radius: 0 0.375rem 0.375rem 0;
}
.manual-content :deep(.manual-data-origin dt) {
    font-size: 0.75rem;
    font-weight: 600;
    opacity: 0.6;
}
.manual-content :deep(.manual-data-origin dd) {
    margin: 0 0 0.25rem;
}
.manual-content :deep(.manual-live-demo) {
    margin: 0.75rem 0;
}
.manual-content :deep(.manual-live-demo-island),
.manual-content :deep(.manual-live-demo--placeholder .manual-live-demo-skeleton) {
    border: 1px solid rgba(128, 128, 128, 0.25);
    border-radius: 0.375rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.manual-content :deep(.manual-live-demo-skeleton) {
    padding: 1.5rem 0.75rem;
    text-align: center;
    font-size: 0.75rem;
    opacity: 0.55;
    background: rgba(128, 128, 128, 0.06);
}
.manual-content :deep(.manual-live-demo figcaption) {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: 0.25rem;
}
</style>
